/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { SelectField } from 'components';
import { Typography, TextField, Grid, Card, Stack, CardHeader, CardContent, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import _ from 'underscore';
import DatePicker from 'components/DatePicker/DatePicker.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import CommentsHistoryTable from './CommentsHistoryTable';

import { SetFormState, GetDropdowns, GetSiteTrackerToEdit, GetCommentsHistory } from '../../actions/SiteTrackerActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	accordion: {
		backgroundColor: '#f5f5f5'
	},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const schema = {
	bsh: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	siteType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	siteName: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	activationDate: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	activationStatus: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	sow: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const SiteTrackerDialogFormEdit = (props) => {
	const { siteTrackerId, confirmLabel, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.siteTrackerReducer.formState);
	const dropdowns = useSelector((state) => state.siteTrackerReducer.dropdowns);
	const commentsHistory = useSelector((state) => state.siteTrackerReducer.commentsHistory);
	const user = useSelector(state => state.session.user);
	const okNokDropdown = [{ value: 'OK', text: 'OK' }, { value: 'NOK', text: 'NOK' }];
	const [expanded, setExpanded] = useState('');
	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _dateChange = (value, id) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[id]: moment(value).format('YYYY-MM-DDTHH:mm:ss'),
			},
			touched: {
				...formState.touched,
				[id]: true,
			},
		});
	};

	useEffect(() => {
		dispatch(GetSiteTrackerToEdit(siteTrackerId));
		dispatch(GetDropdowns());
		dispatch(GetCommentsHistory(siteTrackerId, 'Comments'));
		dispatch(GetCommentsHistory(siteTrackerId, 'D7NewCellsReportComments'));
		dispatch(GetCommentsHistory(siteTrackerId, 'D7PCVReportComments'));
		dispatch(GetCommentsHistory(siteTrackerId, 'D1BISReportComments'));
		dispatch(GetCommentsHistory(siteTrackerId, 'D1PCVReportComments'));
	}, []);

	useEffect(() => {
		if (formState.submit && formState.values.d1Status !== 'OK' && formState.values.d7ReportStatusDate !== null) {
			setFormState({
				...formState,
				values: {
					...formState.values,
					d7ReportStatusDate: null,
				},
			});
		}
	}, [formState.values.d1Status]);

	useMemo(() => {
		const newTouched = {};

		if (formState.submit) {
			_.each(_.keys(schema), item => {
				newTouched[item] = true;
			});
		}

		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			}
		});
	}, [formState.values, formState.submit]);

	return (
		<div className={classes.fields}>
			<Grid container spacing={2}>
				<Grid item md={8} xs={8}>
					<Grid container spacing={2}>
						<Grid item md={4} xs={4}>
							<DatePicker
								id="activationDate"
								label="Activation Date"
								error={_hasError('activationDate')}
								inputFormat="YYYY-MM-DD"
								value={formState.values.rescheduledDate ? formState.values.rescheduledDate : formState.values.activationDate}
								onChange={(value) => _dateChange(value, 'rescheduledDate')}
								disabled={(!formState.touched.activationStatus || formState.values.activationStatus !== 'Rescheduled')}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="activationStatus"
								name="activationStatus"
								hintText="Activation Status"
								error={_hasError('activationStatus')}
								helperText={
									_hasError('activationStatus')
										? formState.errors.activationStatus[0]
										: null
								}
								defaultValue=""
								menuItems={dropdowns.activationStatus || []}
								value={formState.values.activationStatus || ''}
								onChange={_handleChange}
								disabled={user.role !== 'Manager' && ((formState.values.activationStatus !== 'Scheduled' && !formState.touched.activationStatus) || formState.values.currentStatus === 'Complete')}
							/>
						</Grid>
						<Grid item md={12} xs={12}>
							<Accordion className={classes.accordion} expanded={expanded === 'panel1'} onChange={_handleAccordionChange('panel1')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography>Comments</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<CommentsHistoryTable data={commentsHistory.Comments} />
									</Grid>
									{!(formState.values.currentStatus === 'Complete') && (
										<TextField
											id="comments"
											name="comments"
											variant="outlined"
											multiline
											minRows={2}
											fullWidth
											value={formState.values.comments || ''}
											InputLabelProps={{
												shrink: true,
											}}
											onChange={_handleChange}
										/>
									)}
								</AccordionDetails>
							</Accordion>
						</Grid>
						{/* New row */}
						<Grid item md={12} xs={12}>
							<Divider><Typography variant="h5" gutterBottom component="div">REPORT D+1</Typography></Divider>
						</Grid>
						<Grid item md={4} xs={4}>
							<TextField
								id="d1ReportOwner"
								name="d1ReportOwner"
								label="D+1 Report Owner"
								error={_hasError('d1ReportOwner')}
								helperText={
									_hasError('d1ReportOwner')
										? formState.errors.d1ReportOwner[0]
										: null
								}
								variant="outlined"
								value={formState.values.d1ReportOwner || ''}
								InputLabelProps={{
									shrink: true,
								}}
								fullWidth
								disabled
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<DatePicker
								id="d1ReportStatusDate"
								label="D+1 Report Status Date"
								error={_hasError('d1ReportStatusDate')}
								inputFormat="YYYY-MM-DD"
								value={formState.values.d1ReportStatusDate}
								onChange={(value) => _dateChange(value, 'd1ReportStatusDate')}
								disabled={user.role !== 'Manager' && formState.values.d1Status === 'OK'}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="d1Status"
								name="d1Status"
								hintText="D+1 Status"
								error={_hasError('d1Status')}
								helperText={
									_hasError('d1Status')
										? formState.errors.d1Status[0]
										: null
								}
								defaultValue=""
								menuItems={dropdowns.d1Status || []}
								value={formState.values.d1Status || ''}
								onChange={_handleChange}
								disabled={user.role !== 'Manager' && (formState.values.currentStatus === 'Complete' || (formState.values.d1Status === 'OK' && !formState.touched.d1Status) || formState.values.activationStatus !== 'Complete' || formState.touched.activationStatus)}
							/>
						</Grid>
						<Grid item md={12} xs={12}>
							<Accordion className={classes.accordion} expanded={expanded === 'panel2'} onChange={_handleAccordionChange('panel2')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography>D+1 PCV Report Comments</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<CommentsHistoryTable data={commentsHistory.D1PCVReportComments} />
									</Grid>
									{!(formState.values.currentStatus === 'Complete') && (
										<TextField
											id="d1PCVReportComments"
											name="d1PCVReportComments"
											variant="outlined"
											multiline
											minRows={2}
											fullWidth
											value={formState.values.d1PCVReportComments || ''}
											InputLabelProps={{
												shrink: true,
											}}
											onChange={_handleChange}
											disabled={formState.values.currentStatus === 'Complete'}
										/>
									)}
								</AccordionDetails>
							</Accordion>
							<Accordion className={classes.accordion} expanded={expanded === 'panel3'} onChange={_handleAccordionChange('panel3')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography>D+1 BIS Report Comments</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<CommentsHistoryTable data={commentsHistory.D1BISReportComments} />
									</Grid>
									{!(formState.values.currentStatus === 'Complete') && (
										<TextField
											id="d1BISReportComments"
											name="d1BISReportComments"
											variant="outlined"
											multiline
											minRows={2}
											fullWidth
											value={formState.values.d1BISReportComments || ''}
											InputLabelProps={{
												shrink: true,
											}}
											onChange={_handleChange}
											disabled={formState.values.currentStatus === 'Complete'}
										/>
									)}
								</AccordionDetails>
							</Accordion>
						</Grid>
						{/* New row */}
						<Grid item md={12} xs={12}>
							<Divider><Typography variant="h5" gutterBottom component="div">REPORT D+7</Typography></Divider>
						</Grid>
						<Grid item md={4} xs={4}>
							<TextField
								id="d7ReportOwner"
								name="d7ReportOwner"
								label="D+7 Report Owner"
								error={_hasError('d7ReportOwner')}
								helperText={
									_hasError('d7ReportOwner')
										? formState.errors.d7ReportOwner[0]
										: null
								}
								variant="outlined"
								value={formState.values.d7ReportOwner || ''}
								InputLabelProps={{
									shrink: true,
								}}
								fullWidth
								disabled
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<DatePicker
								id="d7ReportStatusDate"
								label="D+7 Report Status Date"
								error={_hasError('d7ReportStatusDate')}
								inputFormat="YYYY-MM-DD"
								value={formState.values.d7ReportStatusDate}
								onChange={(value) => _dateChange(value, 'd7ReportStatusDate')}
								disabled={user.role !== 'Manager' && ((formState.values.d7Status !== 'NOK' && !formState.touched.d7Status && formState.values.d7Status) || formState.values.d1Status !== 'OK' || formState.touched.d1Status)}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="d7Status"
								name="d7Status"
								hintText="D+7 Status"
								error={_hasError('d7Status')}
								helperText={
									_hasError('d7Status')
										? formState.errors.d7Status[0]
										: null
								}
								defaultValue=""
								menuItems={dropdowns.d7Status || []}
								value={formState.values.d7Status || ''}
								onChange={_handleChange}
								disabled={user.role !== 'Manager' && (formState.values.currentStatus === 'Complete' || (formState.values.d7Status !== 'NOK' && !formState.touched.d7Status && formState.values.d7Status) || formState.values.d1Status !== 'OK' || formState.touched.d1Status)}
							/>
						</Grid>
						<Grid item md={12} xs={12}>
							<Accordion className={classes.accordion} expanded={expanded === 'panel4'} onChange={_handleAccordionChange('panel4')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography>D+7 PCV Report Comments</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<CommentsHistoryTable data={commentsHistory.D7PCVReportComments} />
									</Grid>
									{!(formState.values.currentStatus === 'Complete') && (
										<TextField
											id="d7PCVReportComments"
											name="d7PCVReportComments"
											variant="outlined"
											multiline
											minRows={2}
											fullWidth
											value={formState.values.d7PCVReportComments || ''}
											InputLabelProps={{
												shrink: true,
											}}
											onChange={_handleChange}
											disabled={formState.values.currentStatus === 'Complete'}
										/>
									)}
								</AccordionDetails>
							</Accordion>
							<Accordion className={classes.accordion} expanded={expanded === 'panel5'} onChange={_handleAccordionChange('panel5')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography>D+7 New Cells Report Comments</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<CommentsHistoryTable data={commentsHistory.D7NewCellsReportComments} />
									</Grid>
									{!(formState.values.currentStatus === 'Complete') && (
										<TextField
											id="d7NewCellsReportComments"
											name="d7NewCellsReportComments"
											variant="outlined"
											multiline
											minRows={2}
											fullWidth
											value={formState.values.d7NewCellsReportComments || ''}
											InputLabelProps={{
												shrink: true,
											}}
											onChange={_handleChange}
											disabled={formState.values.currentStatus === 'Complete'}
										/>
									)}
								</AccordionDetails>
							</Accordion>
						</Grid>
						{/* New row */}
						<Grid item md={12} xs={12}>
							<Divider><Typography variant="h5" gutterBottom component="div">Claim TTO</Typography></Divider>
						</Grid>
						<Grid item md={4} xs={4}>
							<DatePicker
								id="ttoSubmitDate"
								label="TTO Submit Date"
								error={_hasError('ttoSubmitDate')}
								inputFormat="YYYY-MM-DD"
								value={formState.values.ttoSubmitDate}
								onChange={(value) => _dateChange(value, 'ttoSubmitDate')}
								disabled
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<DatePicker
								id="ttoApprovedDate"
								label="TTO Approved Date"
								error={_hasError('ttoApprovedDate')}
								inputFormat="YYYY-MM-DD"
								value={formState.values.ttoApprovedDate}
								onChange={(value) => _dateChange(value, 'ttoApprovedDate')}
								disabled={user.role !== 'Manager' && (formState.values.ttoSubmitDate === null || formState.values.currentStatus === 'Complete')}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item md={4} xs={4}>
					<Card>
						<CardHeader
							title="Status"
							titleTypographyProps={{ align: 'center' }}
						/>
						<CardContent>
							<Stack spacing={2}>
								{/* New row */}
								<TextField
									id="actionStatus"
									name="actionStatus"
									label="Action Status"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.actionStatus || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="currentStatus"
									name="currentStatus"
									label="Current Status"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.currentStatus || ''}
									InputLabelProps={{
										shrink: true,
										classes: { notchedOutline: classes.noBorder }
									}}
								/>
							</Stack>
						</CardContent>
					</Card>
					<Card>
						<CardHeader
							title="Details"
							titleTypographyProps={{ align: 'center' }}
						/>
						<CardContent>
							<Stack spacing={2}>
								{/* New row */}
								<TextField
									id="key"
									name="key"
									label="Key"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.key || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="lcs"
									name="lcs"
									label="LCS"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.lcs || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="dasZone"
									name="dasZone"
									label="DAS Zone"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.dasZone || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="bsh"
									name="bsh"
									label="BSH"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.bsh || ''}
									InputLabelProps={{
										shrink: true,
										classes: { notchedOutline: classes.noBorder }
									}}
								/>
								{/* New row */}
								<TextField
									id="siteName"
									name="siteName"
									label="Site Name"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.siteName || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="siteType"
									name="siteType"
									label="Site Type"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.siteType || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="sow"
									name="sow"
									label="SOW"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.sow || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="project"
									name="project"
									label="Project"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.project || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="vendor"
									name="vendor"
									label="Vendor"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.vendor || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="fieldPartner"
									name="fieldPartner"
									label="Field Partner"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.fieldPartner || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>

							</Stack>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

SiteTrackerDialogFormEdit.propTypes = {
	siteTrackerId: PropTypes.number,
	confirmLabel: PropTypes.string,
};

export default SiteTrackerDialogFormEdit;
